// #9420: Generated SEP XML preview / manual override editor / clear-reset dialog.
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { telemetry } from "globals/endpoints";
import { operationURLS } from "../model";
import loggerFactory from "globals/logger/logger-factory";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import { useMaterialUIController } from "context";
import CTLDialogContent from "application/components/styles/CTLDialogContent";

const componentName = "XmlEditorDialog";

function XmlEditorDialog({ open, onClose, macAddress, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [xml, setXml] = useState("");
  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [editedBy, setEditedBy] = useState(null);
  const [editedAt, setEditedAt] = useState(null);
  const [error, setError] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const viewOnly = AuthenticationService.isViewOnlyUser(); // #9420: preview-only for view-only users
  const [controller] = useMaterialUIController(); // #9420: respect dark/light theme
  const { darkMode } = controller;

  useEffect(() => {
    if (open && macAddress) {
      setError("");
      setConfirmReset(false);
      setLoading(true);
      telemetry
        .post(`${operationURLS.previewXml}/${macAddress}`)
        .then((response) => {
          const data = response?.data?.data || {};
          setXml(data.xml || "");
          setManuallyEdited(Boolean(data.xmlManuallyEdited));
          setEditedBy(data.xmlEditedBy || null);
          setEditedAt(data.xmlEditedAt || null);
        })
        .catch((e) => {
          loggerFactory.error(componentName, "preview fetch failed", e);
          setError("Unable to load generated XML for this device.");
        })
        .finally(() => setLoading(false));
    }
  }, [open, macAddress]);

  const handleSave = () => {
    setError("");
    if (!xml || !xml.trim()) {
      setError("XML content is empty.");
      return;
    }
    setSaving(true);
    telemetry
      .post(`${operationURLS.saveXmlOverride}/${macAddress}`, { xml })
      .then((response) => {
        if (response?.data?.status === 0) {
          setManuallyEdited(true);
          if (onSaved) onSaved(response.data.message || "Manual XML override saved.");
          onClose();
        } else {
          setError(response?.data?.message || "Invalid or unsafe XML; rejected.");
        }
      })
      .catch((e) => {
        loggerFactory.error(componentName, "save override failed", e);
        setError(e?.response?.data?.message || "Failed to save manual XML override.");
      })
      .finally(() => setSaving(false));
  };

  const handleReset = () => {
    setError("");
    setSaving(true);
    telemetry
      .post(`${operationURLS.clearXmlOverride}/${macAddress}`)
      .then((response) => {
        if (response?.data?.status === 0) {
          if (onSaved) onSaved(response.data.message || "Manual edit cleared; XML regenerated.");
          onClose();
        } else {
          setError(response?.data?.message || "Failed to clear manual edit.");
        }
      })
      .catch((e) => {
        loggerFactory.error(componentName, "clear override failed", e);
        setError("Failed to clear manual edit.");
      })
      .finally(() => {
        setSaving(false);
        setConfirmReset(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        Device XML — {macAddress}
      </DialogTitle>
      <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        {manuallyEdited && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This profile is manually edited and is skipped by bulk provisioning. Structured-config saves will not
            update the live XML until the manual edit is cleared / reset to generated.
            {editedBy ? ` Last edited by ${editedBy}` : ""}
            {editedAt ? ` at ${new Date(Number(editedAt)).toLocaleString()}.` : ""}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </MDBox>
        ) : (
          <TextField
            label="Generated / Override XML"
            multiline
            minRows={16}
            fullWidth
            value={xml}
            onChange={(e) => setXml(e.target.value)}
            InputProps={{
              readOnly: viewOnly,
              style: { fontFamily: "monospace", fontSize: "0.8rem" },
            }}
            sx={{
              // #9420: keep the XML readable in both dark and light theme
              "& .MuiInputBase-root": {
                color: darkMode ? "#ffffff" : "inherit",
                backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "inherit",
              },
              "& .MuiInputBase-input": { color: darkMode ? "#ffffff" : "inherit" },
              "& .MuiInputLabel-root": { color: darkMode ? "#ffffff" : "inherit" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: darkMode ? "rgba(255,255,255,0.3)" : "inherit",
              },
            }}
          />
        )}
        {confirmReset && (
          <MDBox mt={2}>
            <MDTypography variant="button" color="error">
              Reset to generated XML? This discards the manual override and re-enables bulk provisioning for this
              device.
            </MDTypography>
          </MDBox>
        )}
      </DialogContent>
      <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
        <MDButton onClick={onClose} color="secondary" variant="outlined" disabled={saving}>
          Close
        </MDButton>
        {!viewOnly &&
          manuallyEdited &&
          (confirmReset ? (
            <MDButton onClick={handleReset} color="error" variant="contained" disabled={saving}>
              Confirm Reset
            </MDButton>
          ) : (
            <MDButton onClick={() => setConfirmReset(true)} color="error" variant="outlined" disabled={saving}>
              Clear Manual Edit / Reset
            </MDButton>
          ))}
        {!viewOnly && (
          <MDButton onClick={handleSave} color="info" variant="contained" disabled={saving || loading}>
            {saving ? "Saving..." : "Save Override"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default XmlEditorDialog;
